import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { getTokenValue, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import PageScrollView from '@/components/ui/PageScrollView';
import { asApiError } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';
import { normalizeSocialLink } from '@/src/utils/validation';

/**
 * ── /profile/edit — the profile a user can change on their own ────────────
 *
 * First name, last name, social link — the three fields that change with no
 * round-trip to confirm them. Email and mobile are NOT here: editing either is
 * a re-verification, so both stay in the auth flow, and the profile screen is
 * where a missing one gets prompted for.
 *
 * `presentation: 'modal'` is declared on the ROOT stack (app/_layout.tsx) — a
 * screen can't lift itself. A sheet rather than a page because it's reached
 * from more than one place; dismissing returns the user to whichever one, so no
 * caller has to know where to send them back to.
 *
 * One `PATCH /me` for all three fields (`updateMe` takes a partial user), so a
 * save can't land the name and drop the link.
 *
 * A social link can be CHANGED but never removed, so the field is required for
 * a user who already has one and optional for a user who doesn't. Nothing here
 * sends null — an empty field is left out of the patch instead, which it can
 * only be when there was no link to begin with.
 *
 * ── Errors are inline, not toasts ──
 * Every other form in the app toasts its failures. This one can't: the root
 * toast host renders UNDERNEATH a natively-presented modal on iOS (see
 * PSToastHost), so the message would be invisible exactly where the user is
 * waiting for it. Field problems go under their field, and a failed save gets
 * its own line above the button.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Says WHY rather than just "required": the field arrived prefilled, so a user
// who empties it is trying to remove a link, not skipping a blank.
const LINK_REQUIRED = "Your social link can't be removed, only changed.";

export default function EditProfile() {
  const router = useRouter();
  const user = useSessionUser();

  // Seeded once, not synced: the route sits behind `Stack.Protected` so a user
  // is always present by the time this mounts, and re-seeding mid-edit would
  // overwrite what's being typed the moment any other screen refreshes /me.
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [socialLink, setSocialLink] = useState(user?.socialMediaLink ?? '');

  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [lastNameError, setLastNameError] = useState<string | undefined>();
  const [socialLinkError, setSocialLinkError] = useState<string | undefined>();
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const firstNameValid = firstName.trim().length > 0;
  const lastNameValid = lastName.trim().length > 0;

  // A link, once given, can be CHANGED but not taken back — so the field is
  // required for a user who already has one and optional for a user who
  // doesn't. That's the whole reason nothing here ever sends null.
  const hasExistingLink = !!user?.socialMediaLink;
  const socialLinkValid = !hasExistingLink || socialLink.trim().length > 0;

  // Nothing to save is a disabled button, not a no-op request. Compared raw
  // (trimmed) rather than normalized, so re-typing the same link as a bare
  // `instagram.com/you` still counts as a change worth sending.
  const isDirty =
    firstName.trim() !== user?.firstName ||
    lastName.trim() !== user?.lastName ||
    socialLink.trim() !== (user?.socialMediaLink ?? '');

  const closeButton = (
    <PSCircleButton onPress={() => router.back()}>
      <PSIcon name={'close'} />
    </PSCircleButton>
  );

  const handleSave = async () => {
    if (!firstNameValid) setFirstNameError('Enter your first name');
    if (!lastNameValid) setLastNameError('Enter your last name');
    if (!socialLinkValid) setSocialLinkError(LINK_REQUIRED);
    if (!firstNameValid || !lastNameValid || !socialLinkValid) return;

    // Checked for emptiness BEFORE normalizing, because `normalizeSocialLink('')`
    // returns the same null an unparseable link does — and by here an empty
    // field can only mean "never had one", which is left out of the patch
    // rather than sent as a null that would clear it.
    const trimmedLink = socialLink.trim();
    let normalizedLink: string | undefined;
    if (trimmedLink.length > 0) {
      const normalized = normalizeSocialLink(trimmedLink);
      if (!normalized) {
        setSocialLinkError(
          'Enter a valid Instagram, X, or LinkedIn profile link.'
        );
        return;
      }
      normalizedLink = normalized;
    }

    setSaveError(undefined);
    setIsSaving(true);
    try {
      const { user: updated } = await authApi.updateMe({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(normalizedLink ? { socialMediaLink: normalizedLink } : {}),
      });
      setRestoredUser(updated);
      router.back();
    } catch (caught) {
      setSaveError(asApiError(caught).message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;
  return (
    <>
      <Head>
        <title>Edit profile · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <PSText.H1 color="$textDark" textAlign="center">
              Edit profile
            </PSText.H1>
          ),
          headerStyle: { backgroundColor: getTokenValue('$background') },
          // ✕, not ←: a sheet is dismissed, it isn't navigated back from.
          headerLeft: () => closeButton,
          unstable_headerLeftItems: () => [
            {
              type: 'custom' as const,
              element: closeButton,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView headerTransparent={false}>
        <YStack gap="$lg">
          <YStack gap="$xs">
            <PSText.Body3 color="$textDarkMuted">First name</PSText.Body3>
            <PSInput
              type="text"
              value={firstName}
              onChangeText={setFirstName}
              spellCheck={false}
              autoCorrect={false}
              error={firstNameError}
              onFocus={() => setFirstNameError(undefined)}
              onBlur={() =>
                setFirstNameError(
                  firstNameValid ? undefined : 'Enter your first name'
                )
              }
            />
          </YStack>

          <YStack gap="$xs">
            <PSText.Body3 color="$textDarkMuted">Last name</PSText.Body3>
            <PSInput
              type="text"
              value={lastName}
              onChangeText={setLastName}
              spellCheck={false}
              autoCorrect={false}
              error={lastNameError}
              onFocus={() => setLastNameError(undefined)}
              onBlur={() =>
                setLastNameError(
                  lastNameValid ? undefined : 'Enter your last name'
                )
              }
            />
          </YStack>

          <YStack gap="$xs">
            <PSText.Body3 color="$textDarkMuted">
              Social media link
            </PSText.Body3>
            <PSInput
              type="url"
              value={socialLink}
              onChangeText={(value) => {
                setSocialLink(value);
                setSocialLinkError(undefined);
              }}
              error={socialLinkError}
              onBlur={() =>
                setSocialLinkError(socialLinkValid ? undefined : LINK_REQUIRED)
              }
            />
          </YStack>

          <YStack gap="$sm">
            {saveError && (
              <PSText.Body3 color="$error">{saveError}</PSText.Body3>
            )}
            <PSButton
              title="Save"
              loading={isSaving}
              loadingTitle="Saving"
              fullWidth
              disabled={
                !isDirty ||
                !firstNameValid ||
                !lastNameValid ||
                !socialLinkValid
              }
              onPress={handleSave}
            />
          </YStack>
        </YStack>
      </PageScrollView>
    </>
  );
}
